import { createAsyncThunk, createSlice, } from '@reduxjs/toolkit'
import { createTeamRequest } from '@shared/services/TeamService';
import { AppState } from '../state/app.state';
import { updateCurrentUser } from '@shared/store/auth.duck';
import { failureNotification, successNotification } from '@shared/store/notification.duck';
import { enrichError } from '@shared/utils/error-helper';
import { TeamEntity } from '@shared/models/team.models';
import { loadAccountSummary } from "@features/settings/store/payments.duck";
import { loadConfig } from "@shared/store/config.duck";

export interface AddTeamDialogState {
  processing: boolean;
  success: boolean;
  addedTeam: TeamEntity | null;
  error: string;
  failedRequest: {
    teamName: string;
    addUserToTeam: boolean;
  }
}

export const initialAddTeamDialogState: AddTeamDialogState = {
  processing: false,
  success: false,
  addedTeam: null,
  error: '',
  failedRequest: {
    teamName: '',
    addUserToTeam: false,
  }
}

export const addNewTeam = createAsyncThunk(
  'addteamDialog/addTeam',
  async (addTeamDetails: any, thunkAPI) => {
    try {
      await thunkAPI.dispatch(setProcessing());
      const response = await createTeamRequest(addTeamDetails.teamName, addTeamDetails.addUserToTeam);
      await thunkAPI.dispatch(setAddedTeam(response));

      if (addTeamDetails.addUserToTeam) {
        await thunkAPI.dispatch(updateCurrentUser(true));
      }

      await thunkAPI.dispatch(loadAccountSummary());
      thunkAPI.dispatch(loadConfig(['teams']));

      await thunkAPI.dispatch(setSuccess());
      await thunkAPI.dispatch(successNotification('Team added'));
    } catch (err: any) {
      const enrichedError = enrichError(err?.message);
      await thunkAPI.dispatch(setError({message: enrichedError.description, details: addTeamDetails}));
      await thunkAPI.dispatch(failureNotification(enrichedError.description))
    }
  }
);

const slice = createSlice({
  name: 'addTeamDialog',
  initialState: initialAddTeamDialogState,
  reducers: {
    setAddedTeam: (state, action) => ({...state, addedTeam: action.payload}),
    setProcessing: (state) => ({...state, processing: true, success: false, error: ''}),
    setSuccess: (state) => ({...state, processing: false, success: true, error: ''}),
    setError: (state, action) => ({
      ...state,
      processing: false,
      success: false,
      error: action.payload.message,
      failedRequest: action.payload.details
    }),
    resetAddTeamDialog: () => ({ ...initialAddTeamDialogState, })
  },
  extraReducers: (builder) => {
    builder.addCase(addNewTeam.fulfilled, (state, action) => {
      return {
        ...state,
      }
    });
  }
})

export default slice.reducer;
export const { resetAddTeamDialog } = slice.actions;
const { setProcessing, setSuccess, setError, setAddedTeam } = slice.actions;

// Selectors
export const selectAddTeamProcessingState = (state: AppState) => ({
  processing: state.addTeamDialog.processing,
  error: state.addTeamDialog.error,
  success: state.addTeamDialog.success,
  failedRequest: state.addTeamDialog.failedRequest,
});
const selectLastAddedTeam = (state: AppState) => state.addTeamDialog.addedTeam;
